"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import {
  Business,
  Contact,
  Household,
  isStorageAttachment,
  isUploadedAttachment,
  LexicalText,
  STARTING_NODES,
  Team,
  User,
} from "@/schema"
import { Mention } from "@/schema/cross_reference"
import { TaskTemplate } from "@/schema/task-template"
import { useGlobalDialogStore } from "@/stores/use-global-dialog-store"
import { usePinnedDialogStore } from "@/stores/use-pinned-dialog-store"
import { useTaskStore } from "@/stores/use-task-store"
import { useVoiceTranscriptionStore } from "@/stores/use-voice-transcription-store"
import { isValidDate } from "@/utils/format-date"
import { INSERT_TABLE_COMMAND } from "@lexical/table"
import { useQueryClient } from "@tanstack/react-query"
import { BookSelectInput } from "@ui/book-select"
import {
  Button,
  DialogFooter,
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@ui/shad"
import TemplateFileSelector from "@ui/template-file-selector"
import {
  CalendarSync,
  Loader2,
  Lock,
  Mic,
  Paperclip,
  PictureInPicture2,
  Plus,
  TableIcon,
  Unlock,
  X,
} from "lucide-react"
import { toast } from "sonner"

import { isAllBooksId } from "@/lib/all-books"
import { useFetchBooks } from "@/hooks/api/books"
import { useFetchTags } from "@/hooks/api/tags/queries"
import { useCreateTask, useCreateTaskRecurrence } from "@/hooks/api/tasks"
import { useFetchCurrentUser, useFetchUsers } from "@/hooks/api/users"
import { useBookId } from "@/hooks/use-book-id"
import { useBookTimezone } from "@/hooks/use-book-timezone"
import { useSingleFlight } from "@/hooks/use-single-flight"
import { MentionCombobox } from "@/components/cross-references/mention-combobox"
import { CustomDialogContent } from "@/components/dialogs/custom-dialog-content"
import { useDialogCloseGuard } from "@/components/dialogs/use-dialog-close-guard"
import { markdownToNodes } from "@/components/lexical/serializer"
import { StorageAttachmentChip } from "@/components/storage/storage-attachment-chip"
import { useMentionUpdates } from "@/components/tasks/hooks/use-mention-updates"
import { PrivacyViewerList } from "@/components/tasks/shared/privacy-viewer-list"
import { dateAtStartOfDayInTimeZone, recurrenceDate } from "@/components/tasks/utils/deadline-payload"
import { CollaboratorSelector } from "@/components/users/collaborator-selector"

import { TaskCreateForm, type TaskCreateFormHandle } from "./task-create-form"
import { TaskTemplatePicker, type TaskTemplateAssignment } from "./task-template-picker"

export interface CreateTaskDialogProps {
  id?: string
  allowRelationshipChange?: boolean
  defaultHousehold?: Household | null
  defaultContact?: Contact | null
  defaultBusiness?: Business | null
  defaultMentions?: Mention[]
  defaultProjectId?: string | null
  defaultProjectMilestoneId?: string | null
  recommendable?: {
    type: string
    id: string
  }
  syncboxThreadId?: string
  prefillData?: {
    title?: string | LexicalText
    description?: string | LexicalText
    deadline_at?: string
    assigned_to_id?: string
  }
  onCreated?: () => void
}

function lexicalHasPlainText(text?: LexicalText): boolean {
  const htmlText = (text?.html ?? "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;|&#8203;|&#x200b;|\u200B/gi, " ")
    .trim()

  return htmlText.length > 0 || (text?.markdown ?? "").trim().length > 0
}

function lexicalHasContent(text?: LexicalText): boolean {
  if (lexicalHasPlainText(text)) return true

  // Structural/media content (pasted image, inserted table, embed) carries no text but
  // is still real content handleCreateTask would submit, so it must count as dirty.
  return /<(img|table|iframe|video|audio)\b/i.test(text?.html ?? "")
}

export default function CreateTaskDialog({
  id = "",
  allowRelationshipChange = false,
  defaultHousehold = null,
  defaultContact = null,
  defaultBusiness = null,
  defaultMentions = [],
  defaultProjectId = null,
  defaultProjectMilestoneId = null,
  recommendable,
  syncboxThreadId,
  prefillData,
  onCreated,
}: CreateTaskDialogProps) {
  const queryClient = useQueryClient()
  const dialogStore = useGlobalDialogStore()
  const { pinnedDialogs, unpinDialog, closeDialog } = usePinnedDialogStore()
  const [draftId, setDraftId] = useState(id)
  const { drafts, createDraft, updateDraft, removeDraft } = useTaskStore()
  const createTaskMutation = useCreateTask()
  const createTaskRecurrenceMutation = useCreateTaskRecurrence()
  const isCreating = createTaskMutation.isPending || createTaskRecurrenceMutation.isPending
  const { begin: beginCreate, end: endCreate } = useSingleFlight()
  const isPinned = pinnedDialogs.some((dialog) => dialog.id === draftId)
  const { data: currentUser } = useFetchCurrentUser()
  const { data: tagsData } = useFetchTags("task")
  const { data: booksData } = useFetchBooks()
  const { data: usersData, isFetched: usersDataFetched } = useFetchUsers()
  const bookId = useBookId()
  const showBookSelector = isAllBooksId(bookId)
  const [editorVersion, setEditorVersion] = useState(0)
  const dialogRef = useRef<HTMLDivElement | null>(null)
  const taskFormRef = useRef<TaskCreateFormHandle | null>(null)
  const lastFocusedEditorRef = useRef<Element | null>(null)
  const {
    trigger: triggerVoiceTranscription,
    isActive: isVoiceActive,
    stop: stopVoiceTranscription,
  } = useVoiceTranscriptionStore()

  useEffect(() => {
    const handleFocusIn = (e: FocusEvent) => {
      const container = taskFormRef.current?.getEditorContainer()
      if (container && e.target instanceof Element && container.contains(e.target)) {
        if (e.target.hasAttribute("contenteditable")) {
          lastFocusedEditorRef.current = e.target
        }
      }
    }

    document.addEventListener("focusin", handleFocusIn)
    return () => document.removeEventListener("focusin", handleFocusIn)
  }, [])

  const draft = draftId ? drafts[draftId] : null
  const draftBookTimezone = useBookTimezone(draft?.bookId ?? bookId)

  const recordId = (record?: { id: string } | null) => record?.id ?? null
  const mentionKey = (mentions?: Mention[]) =>
    (mentions ?? [])
      .map((mention) => `${mention.target_type}:${mention.target_id}`)
      .sort()
      .join(",")
  const userIdKey = (users?: { id: string }[]) =>
    (users ?? [])
      .map((user) => user.id)
      .sort()
      .join(",")

  // Collaborators are seeded with the current user exactly once, guarded by
  // initialCollaboratorsSet; before that runs the baseline is empty.
  const seededCollaborators = draft?.initialCollaboratorsSet && currentUser ? [currentUser] : []

  // Dirty when the user has entered anything that handleCreateTask would submit.
  // Fields that start empty — priority/label/team/custom-field assignee/recurrence/
  // privacy/files — are dirty as soon as they hold a value. Auto-seeded fields (date,
  // assignee, relationships, collaborators) are compared against the baseline captured
  // when the dialog opened, so an untouched "Add task" reads clean while a later edit
  // still registers — and the comparison survives a pin/reopen.
  const isDirty =
    lexicalHasContent(draft?.title) ||
    lexicalHasContent(draft?.description) ||
    draft?.priority != null ||
    draft?.label != null ||
    draft?.assignedTeam != null ||
    draft?.assignedToCustomFieldId != null ||
    recordId(draft?.assignee) !== (draft?.baselineAssigneeId ?? null) ||
    draft?.recurrenceEnabled === true ||
    draft?.isPrivate === true ||
    (draft?.files?.length ?? 0) > 0 ||
    (draft?.templateAttachments?.length ?? 0) > 0 ||
    (draft?.templateStorageAttachments?.length ?? 0) > 0 ||
    draft?.taskTemplateId != null ||
    (draft?.date?.getTime() ?? null) !== (draft?.baselineDateMs ?? null) ||
    recordId(draft?.household) !== (draft?.baselineHouseholdId ?? null) ||
    recordId(draft?.contact) !== (draft?.baselineContactId ?? null) ||
    recordId(draft?.business) !== (draft?.baselineBusinessId ?? null) ||
    mentionKey(draft?.mentions) !== (draft?.baselineMentionKey ?? "") ||
    (draft?.bookId ?? null) !== (draft?.baselineBookId ?? null) ||
    userIdKey(draft?.collaborators) !== userIdKey(seededCollaborators)

  const [selectedTeam, setSelectedTeam] = useState<Team | null>(draft?.assignedTeam ?? null)
  const [selectedAssignedToCustomFieldId, setSelectedAssignedToCustomFieldId] = useState<string | null>(
    draft?.assignedToCustomFieldId ?? null
  )

  const {
    selectedHousehold,
    selectedContact,
    selectedBusiness,
    selectedAssignee,
    selectedCollaborators,
    setSelectedContact,
    setSelectedAssignee,
    setSelectedCollaborators,
    setSelectedHousehold,
    setSelectedBusiness,
    handleMentionsUpdate,
  } = useMentionUpdates({
    defaultHousehold: draft?.household ?? defaultHousehold,
    defaultContact: draft?.contact ?? defaultContact,
    defaultBusiness: draft?.business ?? defaultBusiness,
    defaultAssignee: draft?.assignee ?? null,
    defaultCollaborators: draft?.collaborators ?? [],
    onRemovePersonMention: (personId: string) => {
      if (!draftId || !draft) return
      updateDraft(draftId, {
        mentions: (draft.mentions || []).filter((m) => !(m.target_id === personId && m.target_type === "Person")),
      })
    },
  })

  const handleMentionsUpdateWrapper = useCallback(
    (updates: import("@/components/tasks/hooks/use-task-mentions").MentionUpdateData) => {
      handleMentionsUpdate(updates)

      if (!draftId || !draft) return

      if (updates.add_mention) {
        const exists = draft.mentions?.some(
          (m) => m.target_id === updates.add_mention!.target_id && m.target_type === updates.add_mention!.target_type
        )
        if (!exists) {
          updateDraft(draftId, {
            mentions: [...(draft.mentions || []), updates.add_mention],
          })
        }
      }

      if (updates.remove_mention) {
        updateDraft(draftId, {
          mentions: (draft.mentions || []).filter(
            (m) =>
              !(
                m.target_id === updates.remove_mention!.target_id &&
                m.target_type === updates.remove_mention!.target_type
              )
          ),
        })
      }
    },
    [handleMentionsUpdate, draftId, draft, updateDraft]
  )

  const handleAssigneeChange = useCallback(
    (assignee: User | null) => {
      if (assignee) {
        setSelectedAssignedToCustomFieldId(null)
        setSelectedTeam(null)
      }

      setSelectedAssignee(assignee)
      setSelectedCollaborators((current) => {
        // When switching away from a team, remove that team's members (keep currentUser)
        const previousTeamUserIds = new Set(selectedTeam?.users?.map((u) => u.id) ?? [])
        const withoutOldTeam =
          assignee && previousTeamUserIds.size > 0
            ? current.filter((c) => !previousTeamUserIds.has(c.id) || c.id === currentUser?.id)
            : current
        // Remove previous individual assignee unless they're the current user
        const filtered = withoutOldTeam.filter((c) => c.id !== selectedAssignee?.id || c.id === currentUser?.id)
        if (!assignee || filtered.some((c) => c.id === assignee.id)) {
          return filtered
        }
        return [...filtered, assignee]
      })
    },
    [
      selectedAssignee?.id,
      setSelectedAssignee,
      setSelectedCollaborators,
      currentUser?.id,
      selectedTeam?.users,
      setSelectedAssignedToCustomFieldId,
      setSelectedTeam,
    ]
  )

  const handleTeamChange = useCallback(
    (team: Team | null) => {
      if (team) {
        setSelectedAssignedToCustomFieldId(null)
        setSelectedAssignee(null)
      }

      setSelectedCollaborators((current) => {
        // Always keep current user and assignee
        const keepIds = new Set<string>()
        if (currentUser?.id) keepIds.add(currentUser.id)
        if (selectedAssignee?.id) keepIds.add(selectedAssignee.id)

        // Remove previous team members (unless they should be kept)
        const previousTeamUserIds = new Set(selectedTeam?.users?.map((u) => u.id) ?? [])
        let updated = current.filter((c) => !previousTeamUserIds.has(c.id) || keepIds.has(c.id))

        // Add new team members
        if (team?.users) {
          const currentIds = new Set(updated.map((c) => c.id))
          const newCollaborators = team.users.filter((u) => !currentIds.has(u.id))
          updated = [...updated, ...newCollaborators]
        }

        return updated
      })
      setSelectedTeam(team)
    },
    [
      setSelectedCollaborators,
      selectedTeam,
      selectedAssignee?.id,
      currentUser?.id,
      setSelectedAssignedToCustomFieldId,
      setSelectedAssignee,
    ]
  )

  const handleAssignedToCustomFieldChange = useCallback(
    (customFieldId: string | null) => {
      setSelectedAssignedToCustomFieldId(customFieldId)

      if (customFieldId) {
        handleAssigneeChange(null)
        handleTeamChange(null)
      }
    },
    [handleAssigneeChange, handleTeamChange]
  )

  const applyTemplateToDraft = useCallback(
    (template: TaskTemplate, assignment: TaskTemplateAssignment) => {
      if (!draftId) return

      const deadlineDate =
        template.deadline_offset === null
          ? undefined
          : (() => {
              const date = new Date()
              date.setHours(0, 0, 0, 0)
              date.setDate(date.getDate() + template.deadline_offset)
              return date
            })()

      const title =
        template.title || ({ markdown: "", preferred_format: "markdown", nodes: STARTING_NODES } as LexicalText)
      const description =
        template.description || ({ markdown: "", preferred_format: "markdown", nodes: STARTING_NODES } as LexicalText)

      // Find the tag object for the template's tag_ids
      const templateTag = template.tag_ids?.[0]
        ? tagsData?.tags?.find((t) => t.id === template.tag_ids?.[0]) || null
        : null

      updateDraft(draftId, {
        title,
        description,
        date: deadlineDate,
        allDay: true,
        templateAttachments: (template.attachments || []).filter(isUploadedAttachment),
        templateStorageAttachments: (template.attachments || []).filter(isStorageAttachment),
        priority: template.priority ?? null,
        isPrivate: template.is_private ?? false,
        label: templateTag,
        taskTemplateId: template.id,
      })

      setEditorVersion((v) => v + 1)

      if (assignment.assignedToCustomFieldId) {
        handleAssigneeChange(null)
        handleTeamChange(null)
        setSelectedAssignedToCustomFieldId(assignment.assignedToCustomFieldId)
      } else if (assignment.assignedTeam) {
        setSelectedAssignedToCustomFieldId(null)
        handleAssigneeChange(null)
        handleTeamChange(assignment.assignedTeam)
      } else {
        setSelectedAssignedToCustomFieldId(null)
        handleTeamChange(null)
        handleAssigneeChange(assignment.assignedUser)
      }
    },
    [draftId, updateDraft, handleAssigneeChange, handleTeamChange, tagsData?.tags]
  )

  const toggleRecurrence = useCallback(() => {
    if (!draftId || !draft) return

    if (draft.recurrenceEnabled) {
      updateDraft(draftId, {
        recurrenceEnabled: false,
        recurrenceIntervalValue: null,
        recurrenceIntervalUnit: null,
        recurrenceMonthlyRuleType: "calendar_day",
        recurrenceWeekday: null,
        recurrenceWeekdayOrdinal: null,
        recurrenceStart: undefined,
        recurrenceEnd: undefined,
      })
      return
    }

    const startSource = draft.recurrenceStart ?? draft.date ?? new Date()
    const normalizedStart = new Date(startSource)
    normalizedStart.setHours(0, 0, 0, 0)

    updateDraft(draftId, {
      recurrenceEnabled: true,
      recurrenceIntervalValue: draft.recurrenceIntervalValue ?? 1,
      recurrenceIntervalUnit: draft.recurrenceIntervalUnit ?? "weeks",
      recurrenceMonthlyRuleType: draft.recurrenceMonthlyRuleType ?? "calendar_day",
      recurrenceWeekday: draft.recurrenceWeekday ?? null,
      recurrenceWeekdayOrdinal: draft.recurrenceWeekdayOrdinal ?? null,
      recurrenceStart: normalizedStart,
      allDay: true,
    })
  }, [draftId, draft, updateDraft])

  const resetForm = useCallback(() => {
    if (draftId) {
      removeDraft(draftId)
    }
    setSelectedCollaborators([])
    setSelectedAssignee(null)
    setSelectedTeam(null)
    setSelectedAssignedToCustomFieldId(null)
    setSelectedHousehold(defaultHousehold)
    setSelectedContact(defaultContact)
    setSelectedBusiness(defaultBusiness)

    if (isPinned) {
      closeDialog(draftId)
    } else {
      dialogStore.closeDialog()
    }
  }, [
    draftId,
    removeDraft,
    setSelectedCollaborators,
    setSelectedAssignee,
    setSelectedTeam,
    setSelectedAssignedToCustomFieldId,
    setSelectedHousehold,
    setSelectedContact,
    setSelectedBusiness,
    defaultHousehold,
    defaultContact,
    defaultBusiness,
    isPinned,
    closeDialog,
    dialogStore,
  ])

  const { requestClose, discardConfirm } = useDialogCloseGuard({
    isDirty,
    entityLabel: "task",
    onClose: resetForm,
    isPinned,
    dialogId: draftId,
  })

  const createNewDraft = useCallback(
    ({
      preserveRelationships,
      preserveCreateMore,
      preserveProjectId,
      preserveProjectMilestoneId,
      preserveBookId,
    }: {
      preserveRelationships: {
        household: Household | null
        contact: Contact | null
        business: Business | null
      }
      preserveCreateMore: boolean
      preserveProjectId: string | null
      preserveProjectMilestoneId: string | null
      preserveBookId: string | null
    }) => {
      const newDraftId = createDraft()

      // createDraft seeds a default date; read it back to record the baseline.
      const seededDate = useTaskStore.getState().drafts[newDraftId]?.date
      updateDraft(newDraftId, {
        household: preserveRelationships.household,
        contact: preserveRelationships.contact,
        business: preserveRelationships.business,
        createMore: preserveCreateMore,
        projectId: preserveProjectId,
        projectMilestoneId: preserveProjectMilestoneId,
        bookId: preserveBookId,
        // Baseline the seeded date and preserved relationships so an untouched
        // create-more draft reads clean, matching a freshly opened dialog.
        baselineDateMs: seededDate?.getTime() ?? null,
        baselineHouseholdId: preserveRelationships.household?.id ?? null,
        baselineContactId: preserveRelationships.contact?.id ?? null,
        baselineBusinessId: preserveRelationships.business?.id ?? null,
        baselineBookId: preserveBookId,
      })

      return newDraftId
    },
    [createDraft, updateDraft]
  )

  const switchToNewDraft = useCallback(
    (
      newDraftId: string,
      oldDraftId: string,
      preserveRelationships: {
        household: Household | null
        contact: Contact | null
        business: Business | null
      }
    ) => {
      initialAssigneeSetRef.current = null
      setDraftId(newDraftId)
      setSelectedCollaborators([])
      setSelectedAssignee(null)
      setSelectedTeam(null)
      setSelectedAssignedToCustomFieldId(null)
      setSelectedHousehold(preserveRelationships.household)
      setSelectedContact(preserveRelationships.contact)
      setSelectedBusiness(preserveRelationships.business)

      if (isPinned) {
        const { updateDialogId } = usePinnedDialogStore.getState()
        updateDialogId(oldDraftId, newDraftId, "task")
      }

      removeDraft(oldDraftId)
    },
    [
      setDraftId,
      setSelectedCollaborators,
      setSelectedAssignee,
      setSelectedTeam,
      setSelectedAssignedToCustomFieldId,
      setSelectedHousehold,
      setSelectedContact,
      setSelectedBusiness,
      isPinned,
      removeDraft,
    ]
  )

  function handlePin() {
    if (!draftId) return

    if (isPinned) {
      unpinDialog(draftId)
    } else {
      dialogStore.pinDialog(draftId, "task", bookId)
    }
  }

  useEffect(() => {
    if (!draftId) {
      const newDraftId = createDraft()
      setDraftId(newDraftId)
      // Initialize the draft with default values
      // createDraft seeds a default date; read it back to record the baseline.
      const seededDate = useTaskStore.getState().drafts[newDraftId]?.date
      updateDraft(newDraftId, {
        household: defaultHousehold,
        contact: defaultContact,
        business: defaultBusiness,
        mentions: defaultMentions,
        projectId: defaultProjectId,
        projectMilestoneId: defaultProjectMilestoneId,
        // Freeze what this draft opened with so dirty-checking survives a pin/reopen,
        // which rebuilds this dialog without its default* props.
        baselineDateMs: seededDate?.getTime() ?? null,
        baselineHouseholdId: defaultHousehold?.id ?? null,
        baselineContactId: defaultContact?.id ?? null,
        baselineBusinessId: defaultBusiness?.id ?? null,
        baselineMentionKey: mentionKey(defaultMentions),
      })
    }
  }, [
    draftId,
    createDraft,
    updateDraft,
    defaultHousehold,
    defaultContact,
    defaultBusiness,
    defaultMentions,
    defaultProjectId,
    defaultProjectMilestoneId,
  ])

  useEffect(() => {
    if (!draftId || !prefillData) return

    const ensureLexical = (value?: string | LexicalText): LexicalText | undefined => {
      if (!value) return undefined
      if (typeof value === "string") {
        const nodes = markdownToNodes(value) || undefined
        return { markdown: value, nodes, preferred_format: "markdown" }
      }
      if (!value.nodes && value.markdown) {
        const nodes = markdownToNodes(value.markdown) || undefined
        return { ...value, nodes }
      }
      return value
    }

    const prefilledDate = prefillData.deadline_at ? new Date(prefillData.deadline_at) : undefined

    updateDraft(draftId, {
      title: ensureLexical(prefillData.title),
      description: ensureLexical(prefillData.description),
      date: prefilledDate,
      // Prefill overwrites whatever createDraft seeded, so rebase the date baseline on it.
      baselineDateMs: prefilledDate?.getTime() ?? null,
    })

    setEditorVersion((v) => v + 1)
  }, [draftId, prefillData, updateDraft])

  const initialAssigneeSetRef = useRef<string | null>(null)
  useEffect(() => {
    if (!draftId) return
    if (initialAssigneeSetRef.current === draftId) return
    // When a prefill assignee is requested, wait for the user list to settle (success or error)
    // before falling back to currentUser, so we don't permanently lock in the wrong assignee.
    if (prefillData?.assigned_to_id && !usersDataFetched) return
    if (!currentUser && !prefillData?.assigned_to_id) return

    // If the user already made an assignment, lock the ref without overwriting their choice.
    if (selectedAssignee || selectedTeam) {
      initialAssigneeSetRef.current = draftId
      return
    }

    initialAssigneeSetRef.current = draftId
    const prefillAssignee =
      prefillData?.assigned_to_id && usersData?.users
        ? usersData.users.find((u) => u.id === prefillData.assigned_to_id)
        : null
    const assignee = prefillAssignee ?? currentUser
    if (assignee?.id) {
      setSelectedAssignee(assignee)
    }
    // Freeze the auto-assigned assignee as the baseline so it doesn't read as a user
    // edit, and so the comparison survives a pin/reopen (which drops prefillData).
    updateDraft(draftId, { baselineAssigneeId: assignee?.id ?? null })
  }, [
    draftId,
    currentUser,
    selectedAssignee,
    selectedTeam,
    setSelectedAssignee,
    prefillData?.assigned_to_id,
    usersData?.users,
    usersDataFetched,
    updateDraft,
  ])

  useEffect(() => {
    if (!draftId || draft?.initialCollaboratorsSet) return
    // Wait for currentUser before marking the baseline seeded — otherwise the flag flips true with
    // no collaborator inserted, and an untouched dialog reads dirty ([] vs a [currentUser] baseline).
    if (!currentUser) return

    if (!selectedCollaborators.some((c) => c.id === currentUser.id)) {
      setSelectedCollaborators([...selectedCollaborators, currentUser])
    }
    updateDraft(draftId, { initialCollaboratorsSet: true })
  }, [
    currentUser,
    selectedCollaborators,
    setSelectedCollaborators,
    updateDraft,
    draft?.initialCollaboratorsSet,
    draftId,
  ])

  useEffect(() => {
    if (!draftId) return
    updateDraft(draftId, {
      assignee: selectedAssignee,
      assignedTeam: selectedTeam,
      assignedToCustomFieldId: selectedAssignedToCustomFieldId,
      collaborators: selectedCollaborators,
      household: selectedHousehold,
      contact: selectedContact,
      business: selectedBusiness,
    })
  }, [
    draftId,
    selectedAssignee,
    selectedTeam,
    selectedAssignedToCustomFieldId,
    selectedCollaborators,
    selectedHousehold,
    selectedContact,
    selectedBusiness,
    updateDraft,
  ])

  const handleCreateTask = useCallback(() => {
    if (!draftId || !draft) return
    // Guard the handler, not just the button — ⌘/Ctrl+Enter reaches this directly.
    if (isCreating) return
    if (draft.date && !isValidDate(draft.date)) return
    if (!lexicalHasPlainText(draft.title)) {
      toast.error("Add a task title")
      taskFormRef.current?.focusEditor()
      return
    }

    const collaboratorIds = draft.collaborators.map((collaborator) => collaborator.id)
    if (draft.assignee) {
      if (!collaboratorIds.includes(draft.assignee.id)) {
        collaboratorIds.push(draft.assignee.id)
      }
    }
    const labelTagIds = draft.label ? [draft.label.id] : []
    const priority = draft.priority ?? null
    const taskableAttrs: {
      taskable_type?: "Contact" | "Household" | "Business"
      taskable_id?: string
    } = {}
    if (draft.contact) {
      taskableAttrs.taskable_type = "Contact"
      taskableAttrs.taskable_id = draft.contact.id
    } else if (draft.household) {
      taskableAttrs.taskable_type = "Household"
      taskableAttrs.taskable_id = draft.household.id
    } else if (draft.business) {
      taskableAttrs.taskable_type = "Business"
      taskableAttrs.taskable_id = draft.business.id
    }

    const handleSuccess = () => {
      toast.success("Task created")
      onCreated?.()
      queryClient.invalidateQueries({ queryKey: ["tasks", "household"] })
      queryClient.invalidateQueries({ queryKey: ["tasks", "contact"] })
      queryClient.invalidateQueries({ queryKey: ["tasks", "business"] })

      const trustRelatedRecord = draft.mentions?.find((record) => record.target_type === "Trust")
      if (trustRelatedRecord) {
        queryClient.invalidateQueries({ queryKey: ["tasks", "trust", trustRelatedRecord.target_id] })
      }

      if (draft.projectId) {
        queryClient.invalidateQueries({ queryKey: ["projects"] })
        queryClient.invalidateQueries({ queryKey: ["project", draft.projectId] })
        queryClient.invalidateQueries({ queryKey: ["tasks", "project", draft.projectId] })
      }

      if (draft.createMore) {
        const preserveRelationships = {
          household: draft.household,
          contact: draft.contact,
          business: draft.business,
        }
        const newDraftId = createNewDraft({
          preserveRelationships,
          preserveCreateMore: draft.createMore,
          preserveProjectId: draft.projectId,
          preserveProjectMilestoneId: draft.projectMilestoneId,
          preserveBookId: draft.bookId,
        })
        switchToNewDraft(newDraftId, draftId, preserveRelationships)
      } else {
        resetForm()
      }
    }

    if (draft.recurrenceEnabled) {
      if (draft.recurrenceIntervalValue == null || !draft.recurrenceIntervalUnit) {
        toast.error("Select how often the task repeats")
        return
      }

      if (
        draft.recurrenceIntervalUnit === "months" &&
        draft.recurrenceMonthlyRuleType === "day_of_week" &&
        (!draft.recurrenceWeekday || !draft.recurrenceWeekdayOrdinal)
      ) {
        toast.error("Select which day of the week the task repeats on")
        return
      }

      const startAt = draft.recurrenceStart ?? new Date(new Date().setHours(0, 0, 0, 0))

      if (draft.recurrenceEnd && draft.recurrenceEnd < startAt) {
        toast.error("End date cannot be before the start date")
        return
      }

      const recurrencePayload = {
        interval_value: draft.recurrenceIntervalValue,
        interval_unit: draft.recurrenceIntervalUnit,
        monthly_rule_type:
          draft.recurrenceIntervalUnit === "months" ||
          (draft.recurrenceIntervalUnit === "years" && draft.recurrenceMonthlyRuleType === "last_day")
            ? draft.recurrenceMonthlyRuleType
            : "calendar_day",
        weekday:
          draft.recurrenceIntervalUnit === "months" && draft.recurrenceMonthlyRuleType === "day_of_week"
            ? draft.recurrenceWeekday
            : null,
        weekday_ordinal:
          draft.recurrenceIntervalUnit === "months" && draft.recurrenceMonthlyRuleType === "day_of_week"
            ? draft.recurrenceWeekdayOrdinal
            : null,
        all_day: true,
        collaborator_ids: collaboratorIds,
        ...taskableAttrs,
        ...(draft.household ? { household_id: draft.household.id } : {}),
        ...(draft.contact ? { contact_id: draft.contact.id } : {}),
        task_template_attributes: {
          assigned_to_id: draft.assignee?.id,
          title: draft.title,
          description: draft.description,
          is_private: draft.isPrivate,
          ...(priority ? { priority } : {}),
          ...(labelTagIds.length > 0 ? { tag_ids: labelTagIds } : {}),
        },
        start_at: recurrenceDate(startAt, true),
        ...(draft.recurrenceEnd ? { recurring_until: recurrenceDate(draft.recurrenceEnd, true) } : {}),
      }

      if (!beginCreate()) return

      createTaskRecurrenceMutation.mutate(
        {
          recurrence: recurrencePayload,
          overrideBookId: draft.bookId ?? undefined,
        },
        {
          onSuccess: handleSuccess,
          onSettled: endCreate,
        }
      )
    } else {
      const templateAttachmentSignedIds = draft.templateAttachments.map((a) => a.signed_id)
      const templateStorageAttachmentIds = draft.templateStorageAttachments.map((a) => a.id)

      if (!beginCreate()) return

      createTaskMutation.mutate(
        {
          task: {
            title: draft.title,
            description: draft.description,
            deadline_at:
              draft.date && draft.allDay ? dateAtStartOfDayInTimeZone(draft.date, draftBookTimezone) : draft.date,
            collaborator_ids: collaboratorIds,
            assigned_to_id: draft.assignee?.id,
            assigned_team_id: draft.assignedTeam?.id,
            assigned_to_custom_field_id: draft.assignedToCustomFieldId ?? undefined,
            ...taskableAttrs,
            ...(draft.household ? { household_id: draft.household.id } : {}),
            ...(draft.contact ? { contact_id: draft.contact.id } : {}),
            all_day: draft.allDay,
            recommendable_type: recommendable?.type,
            recommendable_id: recommendable?.id,
            syncbox_thread_id: syncboxThreadId,
            ...(priority ? { priority } : {}),
            ...(labelTagIds.length > 0 ? { tag_ids: labelTagIds } : {}),
            mentions: draft.mentions,
            files: draft.files.length > 0 ? draft.files : undefined,
            template_attachment_signed_ids:
              templateAttachmentSignedIds.length > 0 ? templateAttachmentSignedIds : undefined,
            template_storage_attachment_ids:
              templateStorageAttachmentIds.length > 0 ? templateStorageAttachmentIds : undefined,
            task_template_id: draft.taskTemplateId ?? undefined,
            project_id: draft.projectId ?? undefined,
            project_milestone_id: draft.projectMilestoneId ?? undefined,
            is_private: draft.isPrivate,
          },
          overrideBookId: draft.bookId ?? undefined,
        },
        {
          onSuccess: handleSuccess,
          onSettled: endCreate,
        }
      )
    }
  }, [
    draftId,
    draft,
    draftBookTimezone,
    isCreating,
    beginCreate,
    endCreate,
    createTaskMutation,
    createTaskRecurrenceMutation,
    queryClient,
    resetForm,
    createNewDraft,
    switchToNewDraft,
    onCreated,
    recommendable,
    syncboxThreadId,
  ])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        if (!(e.target instanceof Node) || !dialogRef.current?.contains(e.target)) return
        e.preventDefault()
        handleCreateTask()
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [draftId, draft, handleCreateTask])

  useEffect(() => {
    // Focus on title when component mounts or draftId changes
    // Use a small timeout to ensure the editor is fully rendered
    const timer = setTimeout(() => {
      taskFormRef.current?.focusEditor()
    }, 100)

    return () => clearTimeout(timer)
  }, [draftId, editorVersion])

  const defaultTaskableBookId = defaultHousehold?.book_id ?? defaultContact?.book_id ?? defaultBusiness?.book_id ?? null
  const hasDefaultTaskable = !!defaultHousehold || !!defaultContact || !!defaultBusiness

  useEffect(() => {
    if (!showBookSelector || draft?.bookId) return
    const initialBookId = defaultTaskableBookId ?? booksData?.books?.[0]?.id
    if (initialBookId) {
      // Baseline the seeded book so switching it later reads dirty, but the initial seed does not.
      updateDraft(draftId, { bookId: initialBookId, baselineBookId: initialBookId })
    }
  }, [showBookSelector, draft?.bookId, defaultTaskableBookId, booksData?.books, draftId, updateDraft])

  if (!draft) return null

  return (
    <CustomDialogContent title="Task">
      <div ref={dialogRef} className="relative flex h-full w-full flex-col">
        <div className="mb-2 flex h-12 items-center gap-2 border-b pr-3 pl-6">
          <div className="grow"></div>
          <CollaboratorSelector
            className=""
            collaboratorIds={selectedCollaborators.map((c) => c.id)}
            onAdd={(user) => {
              if (!selectedCollaborators.some((c) => c.id === user.id)) {
                setSelectedCollaborators([...selectedCollaborators, user])
              }
            }}
            onRemove={(userId) => {
              setSelectedCollaborators(selectedCollaborators.filter((c) => c.id !== userId))
            }}
            triggerButton={
              <Button className="" size="iconXs" variant="ghost">
                <Plus className="text-icon" />
              </Button>
            }
          />
          <Tooltip>
            <TooltipTrigger asChild>
              <Button className="-mr-1.5" size="iconXs" variant={isPinned ? "secondary" : "ghost"} onClick={handlePin}>
                <PictureInPicture2 className="text-icon" />
                <span className="sr-only">{isPinned ? "Unpin" : "Pin to screen"}</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>{isPinned ? "Unpin" : "Pin to screen"}</TooltipContent>
          </Tooltip>
          <Button size="iconXs" variant="ghost" onClick={requestClose}>
            <X className="text-icon" />
          </Button>
        </div>
        <div className="px-5 pt-2 pb-3">
          <TaskCreateForm
            ref={taskFormRef}
            draft={draft}
            editorKey={draftId ? `${draftId}-${editorVersion}` : `${editorVersion}`}
            onDraftChange={(updates) => {
              if (!draftId) return
              updateDraft(draftId, updates)
            }}
            selectedHousehold={selectedHousehold}
            onHouseholdChange={(household) => {
              if (!draftId) return
              setSelectedHousehold(household)
            }}
            selectedContact={selectedContact}
            onContactChange={(contact) => {
              if (!draftId) return
              setSelectedContact(contact)
            }}
            selectedBusiness={selectedBusiness}
            onBusinessChange={(business) => {
              if (!draftId) return
              setSelectedBusiness(business)
            }}
            selectedAssignee={selectedAssignee}
            onAssigneeChange={handleAssigneeChange}
            selectedTeam={selectedTeam}
            onTeamChange={handleTeamChange}
            selectedAssignedToCustomFieldId={selectedAssignedToCustomFieldId}
            onAssignedToCustomFieldChange={handleAssignedToCustomFieldChange}
            onMentionsUpdate={handleMentionsUpdateWrapper}
            allowHouseholds={allowRelationshipChange || (!defaultContact && !defaultBusiness)}
            allowContacts={allowRelationshipChange || (!defaultHousehold && !defaultBusiness)}
            allowBusinesses={allowRelationshipChange || (!defaultHousehold && !defaultContact)}
            relationshipSelectorDisabled={
              !allowRelationshipChange && (!!defaultHousehold || !!defaultContact || !!defaultBusiness)
            }
            relationshipBookId={showBookSelector ? (draft.bookId ?? undefined) : undefined}
            assignmentBookId={showBookSelector ? (draft.bookId ?? undefined) : undefined}
          />
        </div>
        <DialogFooter className="round flex w-full items-center justify-between p-3">
          <div className="flex items-center gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="iconXs"
                    variant={draft.recurrenceEnabled ? "secondary" : "ghost"}
                    onClick={toggleRecurrence}
                    aria-pressed={draft.recurrenceEnabled}
                    type="button"
                    aria-label="Turn repeating on or off"
                  >
                    <CalendarSync className="text-icon h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top" align="start">
                  Turn repeating on / off
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <HoverCard openDelay={150} closeDelay={100}>
              <HoverCardTrigger asChild>
                <Button
                  size="iconXs"
                  variant={draft.isPrivate ? "secondary" : "ghost"}
                  onClick={() => {
                    if (!draftId) return
                    updateDraft(draftId, { isPrivate: !draft.isPrivate })
                  }}
                  aria-pressed={draft.isPrivate}
                  type="button"
                  aria-label={draft.isPrivate ? "Make task public" : "Mark task as private"}
                >
                  {draft.isPrivate ? <Lock className="text-icon h-4 w-4" /> : <Unlock className="text-icon h-4 w-4" />}
                </Button>
              </HoverCardTrigger>
              <HoverCardContent side="top" align="start" className="w-64 p-3">
                {draft.isPrivate ? (
                  <PrivacyViewerList
                    creatorId={currentUser?.id ?? null}
                    assigneeId={selectedAssignee?.id ?? null}
                    teamId={selectedTeam?.id ?? null}
                    collaboratorIds={selectedCollaborators.map((c) => c.id)}
                  />
                ) : (
                  <div className="text-xs">
                    <div className="text-foreground font-semibold">Visible to everyone</div>
                    <div className="text-muted-foreground mt-1">Click the lock to make this task private.</div>
                  </div>
                )}
              </HoverCardContent>
            </HoverCard>
            <TaskTemplatePicker
              onApply={(template, assignment) => {
                applyTemplateToDraft(template, assignment)
              }}
            />
            <MentionCombobox
              mentions={draft.mentions || []}
              onChange={(refs: Mention[]) => {
                if (!draftId) return
                updateDraft(draftId, { mentions: refs })
              }}
            />
            {draft.templateAttachments.map((attachment) => (
              <div key={attachment.signed_id} className="flex h-7 items-center gap-1 rounded-md border px-2 text-xs">
                <Paperclip className="size-3" />
                <span className="max-w-[100px] truncate">{attachment.file_name}</span>
                <button
                  type="button"
                  onClick={() => {
                    if (!draftId) return
                    updateDraft(draftId, {
                      templateAttachments: draft.templateAttachments.filter(
                        (a) => a.signed_id !== attachment.signed_id
                      ),
                    })
                  }}
                  className="text-icon hover:text-destructive ml-0.5 rounded-sm"
                >
                  <X className="size-3" />
                </button>
              </div>
            ))}
            {draft.templateStorageAttachments.map((attachment) => (
              <StorageAttachmentChip
                key={attachment.id}
                attachment={attachment}
                onRemove={() => {
                  if (!draftId) return
                  updateDraft(draftId, {
                    templateStorageAttachments: draft.templateStorageAttachments.filter((a) => a.id !== attachment.id),
                  })
                }}
              />
            ))}
            <TemplateFileSelector
              files={draft.files}
              onFilesChange={(files) => {
                if (!draftId) return
                updateDraft(draftId, { files })
              }}
              existingCount={draft.templateAttachments.length + draft.templateStorageAttachments.length}
              iconOnly
            />
            <Button
              size="iconXs"
              variant="ghost"
              type="button"
              tooltip="Insert table"
              onClick={() => {
                const editor = taskFormRef.current?.getDescriptionEditor()
                if (!editor) return
                editor.focus()
                editor.dispatchCommand(INSERT_TABLE_COMMAND, {
                  rows: "2",
                  columns: "2",
                  includeHeaders: { rows: true, columns: false },
                })
              }}
            >
              <TableIcon className="text-icon h-4 w-4" />
            </Button>
            <Button
              size="iconXs"
              variant={isVoiceActive ? "secondary" : "ghost"}
              onClick={() => {
                if (isVoiceActive) {
                  stopVoiceTranscription()
                } else {
                  const targetElement = lastFocusedEditorRef.current
                  if (targetElement && targetElement instanceof HTMLElement) {
                    targetElement.focus()
                  } else {
                    taskFormRef.current?.focusEditor()
                  }
                  setTimeout(() => triggerVoiceTranscription(), 50)
                }
              }}
              type="button"
            >
              <Mic className={isVoiceActive ? "text-red-accent h-4 w-4 animate-pulse" : "text-icon h-4 w-4"} />
            </Button>
            {showBookSelector && !hasDefaultTaskable && (
              <BookSelectInput
                value={draft.bookId}
                onChange={(bookId) => {
                  if (!draftId) return
                  if (bookId !== draft.bookId) {
                    setSelectedHousehold(null)
                    setSelectedContact(null)
                    setSelectedBusiness(null)
                    setSelectedTeam(null)
                    setSelectedAssignedToCustomFieldId(null)
                    if (selectedAssignee?.id !== currentUser?.id) {
                      setSelectedAssignee(null)
                    }
                  }
                  updateDraft(draftId, {
                    bookId,
                    ...(bookId !== draft.bookId && { projectId: null, projectMilestoneId: null }),
                  })
                }}
              />
            )}
          </div>
          <div className="-my-1! flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Switch
                checked={draft.createMore}
                onCheckedChange={(checked) => {
                  if (!draftId) return
                  updateDraft(draftId, { createMore: checked })
                }}
              />
              <span className="text-foreground text-xs">Create more</span>
            </div>
            <Button size="sm" onClick={handleCreateTask} disabled={isCreating}>
              {isCreating ? <Loader2 className="animate-spin" /> : "Create"}
            </Button>
          </div>
        </DialogFooter>
        {discardConfirm}
      </div>
    </CustomDialogContent>
  )
}
